import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuiz } from '@/context/QuizContext';
import { quizQuestions } from '@/data/quizQuestions';
import { ChevronRight } from 'lucide-react';

const QuizPage = () => {
  const navigate = useNavigate();
  const { addAnswer, answers } = useQuiz();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showMicrocopy, setShowMicrocopy] = useState(false);

  const question = quizQuestions[currentQuestion];
  const totalQuestions = quizQuestions.length;

  // Track QuizStart on first render
  useEffect(() => {
    if (window.fbq) {
      window.fbq('trackCustom', 'QuizStart', {
        content_name: 'Commercial Diagnosis Quiz',
        page: 'QuizPage'
      });
    }
  }, []);

  // Calculate deceiving progress (non-linear)
  const calculateProgress = (current) => {
    if (current <= 5) {
      return (current / 5) * 60;
    } else if (current <= 10) {
      return 60 + ((current - 5) / 5) * 20;
    } else {
      return 80 + ((current - 10) / 5) * 20;
    }
  };

  const progress = calculateProgress(currentQuestion);

  useEffect(() => {
    // Reset selection when question changes
    setSelectedOption(null);
    setShowMicrocopy(false);
  }, [currentQuestion]);

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setShowMicrocopy(true);

    // Add answer to context
    addAnswer(question.id, option.label, option.points);

    // Auto-advance after showing microcopy
    setTimeout(() => {
      if (currentQuestion < totalQuestions - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Quiz completed - track event
        if (window.fbq) {
          window.fbq('trackCustom', 'QuizComplete', {
            content_name: 'Commercial Diagnosis Quiz',
            page: 'QuizPage',
            total_questions: totalQuestions
          });
        }
        navigate('/form');
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#262626] z-50">
        <motion.div
          className="h-full bg-[#802ef8]"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5 }}
          data-testid="quiz-progress-bar"
        />
      </div>

      {/* Question Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-16 sm:py-20 md:py-24">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              {/* Question Text */}
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-8 sm:mb-10 md:mb-12 tracking-tight leading-tight"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid={`question-${question.id}`}
              >
                {question.question}
              </h2>

              {/* Options */}
              <div className="space-y-3 sm:space-y-4">
                {question.options.map((option, index) => (
                  <motion.button
                    key={option.label}
                    onClick={() => handleOptionSelect(option)}
                    disabled={selectedOption !== null}
                    className={`
                      w-full text-left p-4 sm:p-5 md:p-6 rounded-md border transition-all
                      ${
                        selectedOption?.label === option.label
                          ? 'border-[#802ef8] bg-[#802ef8]/10'
                          : 'border-[#262626] bg-[#141414] hover:border-[#802ef8]/50 hover:bg-[#141414]/80'
                      }
                      disabled:cursor-wait
                    `}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    data-testid={`quiz-option-${option.label}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <span className="text-[#802ef8] font-bold text-lg sm:text-xl flex-shrink-0">
                        {option.label}
                      </span>
                      <span className="text-sm sm:text-base md:text-lg text-[#ffffff]">
                        {option.text}
                      </span>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Microcopy */}
              <AnimatePresence>
                {showMicrocopy && selectedOption && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 sm:mt-6 p-3 sm:p-4 bg-[#141414] border border-[#262626] rounded-md"
                    data-testid="quiz-microcopy"
                  >
                    <p className="text-[#a3a3a3] italic text-xs sm:text-sm md:text-base">
                      {selectedOption.microcopy}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default QuizPage;